// src/data/publicProviders.ts
//
// AI-SEO-PROVIDER-CANONICAL-DEDUP-AND-EXPANSION-001
//
// OWNER-CURATED PUBLIC PROVIDER SNAPSHOT — the single source of truth for the
// public /doctors/<slug> profile pages, the /our-providers directory, the XML +
// HTML sitemaps, and the full-body prerender. It is an ALLOWLIST: only the nine
// providers below may ever render publicly, regardless of what the provider
// database contains.
//
// Provenance: every fact was verified READ-ONLY against the LIVE authoritative
// records (approved_providers + doctor_profiles, both is_active AND is_published)
// on lastVerified. Where authoritative sources disagreed on an optional fact the
// disputed value was OMITTED (conservative), never broadened:
//   - Robert Staaf:   PA and NH each appeared in only one source -> omitted (30 states kept).
//   - Michelle Lafferty: VA appeared in one source only -> omitted (NC, PA kept); no NPI exists.
//   - Stephanie White: PsyD/DCSW/BCD titles dropped -> conservative LCSW; states agreed (24).
//   - Lytara Garcia:  SAP/CEAP dropped -> conservative LCSW; "ESA Mental Health Specialist" = role label.
// No private fields (email/phone/address/internal id/private license numbers) live here.
// This file MUST NOT contain the live active/published GATE for production — see
// src/lib/publicProviderDirectory.ts. `snapshotActive`/`snapshotPublished` are the
// LAST-VERIFIED status used ONLY as the synchronous seed for the SEO prerender
// (renderToStaticMarkup runs no effects, so an RPC-only list would prerender
// empty) and MUST NOT override a negative LIVE status.
//
// LIVE-PUBLIC-PAGES-...-PROVIDER-FIX-001 amends the role of this file:
//   EDITORIAL content  -> this file (bio, title, role, normalised state codes,
//                         verified NPI). Curated because the raw DB rows mix
//                         full state names with codes and contain at least one
//                         invalid NPI, which would render a false credential.
//   VISIBILITY + PHOTO -> public.get_public_provider_directory() (authoritative).
// There is deliberately NO per-provider hide list. Being absent from the live
// directory is the ONLY way a curated provider stops rendering.

export interface PublicProvider {
  /** Clean canonical slug — the public URL is /doctors/<slug>. */
  slug: string;
  /** Authoritative approved_providers.slug (the -hex alias for the 4 flagship providers). */
  dbSlug: string;
  name: string;
  /** Post-nominal shown after the name in the H1 (e.g. "LCSW"). */
  title: string;
  /** Role / credential subtitle (e.g. "Licensed Clinical Social Worker"). Not a Physician type. */
  role: string;
  /** Neutral, verified-facts-only biography. No experience/specialties/awards/quotes/"Dr.". */
  bio: string;
  /** Verified public NPI, or null when none is verified for this exact provider. */
  npi: string | null;
  /** Exact verified licensed/service state CODES (conservative — disputed states omitted). */
  states: string[];
  /** Neutral highlight chips. */
  highlights: string[];
  /** Repo-safe public asset path, or null -> component renders a branded initials avatar. */
  image: string | null;
  /** True for the four providers added in this task (initials avatars until owner uploads photos). */
  isNew: boolean;
  /** ISO date the facts were last verified against LIVE authoritative records. */
  lastVerified: string;
  /** Last-verified authoritative status. TEST-only fallback; never overrides negative LIVE status. */
  snapshotActive: boolean;
  snapshotPublished: boolean;
}

export const PROVIDER_SNAPSHOT_VERIFIED = "2026-07-21";

// LIVE-PUBLIC-PAGES-...-PROVIDER-FIX-001 — date the active/published STATUS of
// every provider below was last re-read from LIVE (approved_providers +
// doctor_profiles). Editorial facts still carry PROVIDER_SNAPSHOT_VERIFIED.
export const PROVIDER_STATUS_VERIFIED = "2026-08-20";

// The curated approved public set — EXACTLY NINE.
export const PUBLIC_PROVIDERS: readonly PublicProvider[] = [
  {
    slug: "robert-staaf",
    dbSlug: "robert-staaf-c9240",
    name: "Robert Staaf",
    title: "LCSW",
    role: "Licensed Clinical Social Worker",
    bio: "Robert Staaf is a Licensed Clinical Social Worker who provides telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. Each evaluation is conducted individually, and a letter is issued only when it is clinically appropriate.",
    npi: "1467172478",
    states: ["AL", "AZ", "CO", "CT", "DC", "FL", "GA", "ID", "IL", "IN", "KY", "ME", "MD", "MA", "MI", "MN", "MS", "MT", "NV", "NJ", "NM", "NC", "OH", "OK", "RI", "SC", "TX", "UT", "VT", "VA"],
    highlights: ["LCSW", "Telehealth Evaluations", "ESA Documentation"],
    image: "/assets/providers/provider-robert-staaf.jpg",
    isNew: false,
    lastVerified: PROVIDER_SNAPSHOT_VERIFIED,
    snapshotActive: true,
    snapshotPublished: true,
  },
  {
    slug: "michelle-lafferty",
    dbSlug: "michelle-lafferty-ff1309",
    name: "Michelle Lafferty",
    title: "LCSW",
    role: "Licensed Clinical Social Worker",
    bio: "Michelle Lafferty is a Licensed Clinical Social Worker who conducts telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. She reviews each client individually and issues a letter only when it is clinically appropriate.",
    npi: null,
    states: ["NC", "PA"],
    highlights: ["LCSW", "Telehealth Evaluations", "ESA Documentation"],
    image: "/assets/providers/provider-michelle-lafferty.jpg",
    isNew: false,
    lastVerified: PROVIDER_STATUS_VERIFIED,
    // Re-verified against LIVE on PROVIDER_STATUS_VERIFIED:
    //   approved_providers.is_active = false
    //   doctor_profiles.is_active    = false
    //   doctor_profiles.is_published = false
    // This is a STATUS transcription, not a per-provider hide rule: the seed
    // simply mirrors what Admin says, and get_public_provider_directory() is
    // authoritative on the client. Re-publishing in Admin restores the card
    // with no code change.
    snapshotActive: false,
    snapshotPublished: false,
  },
  {
    slug: "lytara-garcia",
    dbSlug: "lytara-garcia-5a39d",
    name: "Lytara Garcia",
    title: "LCSW",
    role: "ESA Mental Health Specialist",
    bio: "Lytara Garcia is a Licensed Clinical Social Worker who provides telehealth mental health evaluations, including emotional support animal (ESA) assessments. Each evaluation is completed individually, and a letter is issued only when it is clinically appropriate.",
    npi: "1386996213",
    states: ["CA", "NV", "AZ", "TX", "UT", "ID", "FL"],
    highlights: ["LCSW", "Telehealth Evaluations", "ESA Documentation"],
    image: "/assets/providers/provider-lytara-garcia.jpg",
    isNew: false,
    lastVerified: PROVIDER_SNAPSHOT_VERIFIED,
    snapshotActive: true,
    snapshotPublished: true,
  },
  {
    slug: "stephanie-white",
    dbSlug: "stephanie-white-0fd45",
    name: "Stephanie White",
    title: "LCSW",
    role: "Licensed Clinical Social Worker",
    bio: "Stephanie White is a Licensed Clinical Social Worker who provides telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. Every evaluation is individual, and a letter is issued only when it is clinically appropriate.",
    npi: "1528423795",
    states: ["AL", "AK", "CA", "CO", "DE", "FL", "GA", "HI", "ID", "IA", "KS", "KY", "LA", "MT", "NE", "NV", "NJ", "ND", "OR", "TX", "VT", "WV", "WI", "WY"],
    highlights: ["LCSW", "Telehealth Evaluations", "ESA Documentation"],
    image: "/assets/providers/provider-stephanie-white.jpg",
    isNew: false,
    lastVerified: PROVIDER_SNAPSHOT_VERIFIED,
    // PROVIDER-CAROUSEL-STEPHANIE-IMAGE-ORDER-LIVE-001 — re-verified READ-ONLY
    // against LIVE on 2026-09-23:
    //   approved_providers.is_active   = false
    //   doctor_profiles.is_published    = false
    //   get_public_provider_directory() does not return her
    // The owner confirmed she is not approved for public website display. Same
    // STATUS transcription as Michelle Lafferty above — no per-page name
    // filter anywhere. This removes her from the snapshot fallback, the
    // prerendered profile, sitemap.xml, the HTML sitemap and /our-providers
    // schema. Her account, assignments and admin record are untouched.
    snapshotActive: false,
    snapshotPublished: false,
  },
  {
    slug: "eve-rosno",
    dbSlug: "eve-rosno",
    name: "Eve Rosno",
    title: "PhD",
    role: "Licensed Psychologist",
    bio: "Eve Rosno is a licensed psychologist who provides telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. Each evaluation is conducted individually, and a letter is issued only when it is clinically appropriate.",
    npi: "1588688816",
    states: ["AL", "AZ", "AR", "CO", "CT", "DE", "FL", "GA", "ID", "IL", "IN", "KS", "KY", "ME", "MD", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NC", "ND", "OH", "OK", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "DC", "WV", "WI", "WY"],
    highlights: ["Licensed Psychologist", "Telehealth Evaluations", "ESA Documentation"],
    image: null,
    isNew: true,
    lastVerified: PROVIDER_SNAPSHOT_VERIFIED,
    snapshotActive: true,
    snapshotPublished: true,
  },
  {
    slug: "henry-smith",
    dbSlug: "henry-smith",
    name: "Henry Smith",
    title: "LCSW",
    role: "Licensed Clinical Social Worker",
    bio: "Henry Smith is a Licensed Clinical Social Worker who provides telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. Each evaluation is conducted individually, and a letter is issued only when it is clinically appropriate.",
    npi: "1336686450",
    states: ["FL", "CO", "NM", "IL", "MA"],
    highlights: ["LCSW", "Telehealth Evaluations", "ESA Documentation"],
    image: null,
    isNew: true,
    lastVerified: PROVIDER_SNAPSHOT_VERIFIED,
    snapshotActive: true,
    snapshotPublished: true,
  },
  {
    slug: "chad-cunningham",
    dbSlug: "chad-cunningham",
    name: "Chad Cunningham",
    title: "LPC",
    role: "Licensed Professional Counselor",
    bio: "Chad Cunningham is a Licensed Professional Counselor who provides telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. Each evaluation is conducted individually, and a letter is issued only when it is clinically appropriate.",
    npi: "1619165966",
    states: ["MO", "SD", "NM", "UT"],
    highlights: ["LPC", "Telehealth Evaluations", "ESA Documentation"],
    image: null,
    isNew: true,
    lastVerified: PROVIDER_SNAPSHOT_VERIFIED,
    snapshotActive: true,
    snapshotPublished: true,
  },
  {
    slug: "karla-delgado",
    dbSlug: "karla-delgado",
    name: "Karla Delgado",
    title: "LMFT",
    role: "Licensed Marriage and Family Therapist",
    bio: "Karla Delgado is a Licensed Marriage and Family Therapist who provides telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. Each evaluation is conducted individually, and a letter is issued only when it is clinically appropriate.",
    npi: "1730728858",
    states: ["CA"],
    highlights: ["LMFT", "Telehealth Evaluations", "ESA Documentation"],
    image: null,
    isNew: true,
    lastVerified: PROVIDER_SNAPSHOT_VERIFIED,
    snapshotActive: true,
    snapshotPublished: true,
  },
  {
    slug: "cassandra-enriquez",
    dbSlug: "cassandra-enriquez",
    name: "Cassandra Enriquez",
    title: "LPC",
    role: "Licensed Professional Counselor",
    bio: "Cassandra Enriquez is a Licensed Professional Counselor who provides telehealth mental health evaluations, including assessments for emotional support animal (ESA) documentation. Each evaluation is completed individually, and a letter is issued only when it is clinically appropriate.",
    npi: "1679316806",
    states: ["SC", "VA"],
    highlights: ["LPC", "Telehealth Evaluations", "ESA Documentation"],
    image: null,
    isNew: true,
    lastVerified: PROVIDER_STATUS_VERIFIED,
    snapshotActive: true,
    snapshotPublished: true,
  },
];

// Clean canonical slugs (exactly 9). Consumed by the route manifest generator,
// the prerender entry, sitemaps, and the entity guard.
export const CURATED_PROVIDER_SLUGS: readonly string[] = PUBLIC_PROVIDERS.map((p) => p.slug);

// LIVE-PUBLIC-PAGES-...-PROVIDER-FIX-001.
// The subset whose LAST-VERIFIED admin status is active + published.
//
// CURATED_PROVIDER_SLUGS still enumerates all eight for the ROUTE CLASSIFIER, so
// /doctors/<unpublished> stays a known route that renders a privacy-safe
// not-found with noindex (rather than becoming a hard 404 that would have to be
// re-added when the owner re-publishes them).
//
// PUBLISHED_PROVIDER_SLUGS is what may be ADVERTISED — prerendered as indexable
// HTML, listed in the sitemaps, and emitted in directory schema. Without this
// split an unpublished provider's full profile is still prerendered with
// "index, follow" and still listed in sitemap.xml, which is exactly the leak
// this task exists to close.
export const PUBLISHED_PROVIDER_SLUGS: readonly string[] = PUBLIC_PROVIDERS
  .filter((p) => p.snapshotActive === true && p.snapshotPublished === true)
  .map((p) => p.slug);

/** Curated providers whose last-verified admin status is active + published. */
export const PUBLISHED_PROVIDERS: readonly PublicProvider[] = PUBLIC_PROVIDERS.filter(
  (p) => p.snapshotActive === true && p.snapshotPublished === true,
);

// NOTE: the former HOMEPAGE_PROVIDER_SLUGS hardcoded four-name strip was removed
// by LIVE-PUBLIC-PAGES-...-PROVIDER-FIX-001. The homepage now derives both its
// cards and its canonical name links from the live-gated visible set, so an
// unpublished provider cannot survive as a stale link.

// Explicitly EXCLUDED from all public surfaces (both the clean and -hex slugs
// must resolve to a privacy-safe 404/noindex). The underlying DB record is
// preserved; this is a public-visibility exclusion only. No reason is exposed.
export const EXCLUDED_PROVIDER_SLUGS: readonly string[] = [
  "edna-kwan",
  "edna-kwan-78e66",
];

const BY_SLUG: ReadonlyMap<string, PublicProvider> = new Map(
  PUBLIC_PROVIDERS.map((p) => [p.slug, p]),
);

/**
 * Resolve a clean slug to a curated public provider, or null. Pure and
 * synchronous so the profile page + prerender render without a Supabase round
 * trip. A null result MUST be treated as "not a public provider" (404/noindex) —
 * this is the allowlist half of the fail-closed contract.
 */
export function getPublicProvider(slug: string | undefined | null): PublicProvider | null {
  if (!slug) return null;
  return BY_SLUG.get(slug.trim().toLowerCase()) ?? null;
}

export function isCuratedProviderSlug(slug: string | undefined | null): boolean {
  return getPublicProvider(slug) !== null;
}
